// Products.jsx — Full product catalog page with filters, sort, occasion, and price range

import React, { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../firebase/config";
import { seedProducts } from "../firebase/seedProducts";
import ProductCard from "../components/ProductCard";

// MUI Icons
import CardGiftcardOutlinedIcon   from "@mui/icons-material/CardGiftcardOutlined";
import RedeemOutlinedIcon         from "@mui/icons-material/RedeemOutlined";
import ToysOutlinedIcon           from "@mui/icons-material/ToysOutlined";
import CasinoOutlinedIcon         from "@mui/icons-material/CasinoOutlined";
import BrushOutlinedIcon          from "@mui/icons-material/BrushOutlined";
import SortOutlinedIcon           from "@mui/icons-material/SortOutlined";
import FilterListIcon             from "@mui/icons-material/FilterList";
import SearchOffIcon              from "@mui/icons-material/SearchOff";
import ArrowForwardIcon           from "@mui/icons-material/ArrowForward";
import HomeOutlinedIcon           from "@mui/icons-material/HomeOutlined";
import AttachMoneyIcon            from "@mui/icons-material/AttachMoney";
import CakeOutlinedIcon           from "@mui/icons-material/CakeOutlined";
import FavoriteBorderIcon         from "@mui/icons-material/FavoriteBorder";
import ChildCareOutlinedIcon      from "@mui/icons-material/ChildCareOutlined";
import CelebrationOutlinedIcon    from "@mui/icons-material/CelebrationOutlined";
import LocalFloristOutlinedIcon   from "@mui/icons-material/LocalFloristOutlined";
import DiamondOutlinedIcon        from "@mui/icons-material/DiamondOutlined";
import TuneIcon                   from "@mui/icons-material/Tune";
import CloseIcon                  from "@mui/icons-material/Close";

const CATEGORIES = [
  { label: "All",         Icon: CardGiftcardOutlinedIcon },
  { label: "Gift Items",  Icon: RedeemOutlinedIcon       },
  { label: "Toys",        Icon: ToysOutlinedIcon         },
  { label: "Games",       Icon: CasinoOutlinedIcon       },
  { label: "Handicrafts", Icon: BrushOutlinedIcon        },
];

const OCCASIONS = [
  { label: "Birthday",    value: "birthday",    Icon: CakeOutlinedIcon           },
  { label: "Anniversary", value: "anniversary", Icon: FavoriteBorderIcon         },
  { label: "Kids",        value: "kids",        Icon: ChildCareOutlinedIcon      },
  { label: "Wedding",     value: "wedding",     Icon: DiamondOutlinedIcon        },
  { label: "Festival",    value: "festival",    Icon: CelebrationOutlinedIcon    },
  { label: "Thank You",   value: "thankyou",    Icon: LocalFloristOutlinedIcon   },
];

const PRICE_RANGES = [
  { label: "All Prices", min: 0,   max: 999999 },
  { label: "Under $25",  min: 0,   max: 25     },
  { label: "$25 – $50",  min: 25,  max: 50     },
  { label: "$50 – $100", min: 50,  max: 100    },
  { label: "$100+",      min: 100, max: 999999 },
];

const SORT_OPTIONS = [
  { label: "Featured",        value: "featured"   },
  { label: "Newest",          value: "newest"     },
  { label: "Price: Low–High", value: "price_asc"  },
  { label: "Price: High–Low", value: "price_desc" },
  { label: "Top Rated",       value: "rating"     },
];

function ProductSkeleton() {
  return (
    <div className="bg-white rounded-3xl overflow-hidden border border-gray-100" style={{ boxShadow: "0 2px 16px rgba(0,0,0,0.06)" }}>
      <div className="skeleton" style={{ height: "220px" }} />
      <div className="p-4 space-y-3">
        <div className="skeleton h-3 rounded-full w-1/3" />
        <div className="skeleton h-4 rounded-full w-3/4" />
        <div className="skeleton h-3 rounded-full w-full" />
        <div className="skeleton h-3 rounded-full w-2/3" />
        <div className="flex justify-between items-center pt-2">
          <div className="skeleton h-5 rounded-full w-16" />
          <div className="skeleton h-8 rounded-xl w-24" />
        </div>
      </div>
    </div>
  );
}

export default function Products() {
  const [products,     setProducts]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState("");
  const [showFilters,  setShowFilters]  = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedCategory = searchParams.get("category") || "All";
  const occasionFilter   = searchParams.get("occasion") || "";
  const sortBy           = searchParams.get("sort")     || "featured";
  const minPrice         = parseFloat(searchParams.get("minPrice") || "0");
  const maxPrice         = parseFloat(searchParams.get("maxPrice") || "999999");

  // ── Fetch ──────────────────────────────────────────────────────────────
  useEffect(() => {
    async function fetchProducts() {
      try {
        setLoading(true);
        const q = query(collection(db, "products"), orderBy("createdAt", "desc"));
        const snapshot = await getDocs(q);
        if (snapshot.empty) {
          await seedProducts();
          const seeded = await getDocs(q);
          setProducts(seeded.docs.map((d) => ({ id: d.id, ...d.data() })));
        } else {
          setProducts(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        }
      } catch (err) {
        console.error(err);
        setError("Failed to load products.");
      } finally {
        setLoading(false);
      }
    }
    fetchProducts();
  }, []);

  // ── Filter + Sort ──────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let result = products.filter((p) => {
      const matchCat     = selectedCategory === "All" || p.category === selectedCategory;
      const matchOcc     = !occasionFilter || (Array.isArray(p.tags) && p.tags.includes(occasionFilter));
      const matchPrice   = p.price >= minPrice && p.price <= maxPrice;
      return matchCat && matchOcc && matchPrice;
    });

    switch (sortBy) {
      case "price_asc":  result = [...result].sort((a, b) => a.price   - b.price);   break;
      case "price_desc": result = [...result].sort((a, b) => b.price   - a.price);   break;
      case "rating":     result = [...result].sort((a, b) => (b.rating || 0) - (a.rating || 0)); break;
      case "newest":     /* already ordered by createdAt desc from Firestore */ break;
      default: break;
    }
    return result;
  }, [products, selectedCategory, occasionFilter, minPrice, maxPrice, sortBy]);

  // ── Param helpers ──────────────────────────────────────────────────────
  function setParam(key, value) {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "All" || value === "featured" || value === "0" || value === "999999") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next);
  }

  function setPriceRange(min, max) {
    const next = new URLSearchParams(searchParams);
    if (min === 0 && max === 999999) {
      next.delete("minPrice");
      next.delete("maxPrice");
    } else {
      next.set("minPrice", min);
      next.set("maxPrice", max);
    }
    setSearchParams(next);
  }

  function clearAllFilters() {
    setSearchParams({});
  }

  const hasFilters = selectedCategory !== "All" || occasionFilter || searchParams.has("minPrice");

  // ── Page title ─────────────────────────────────────────────────────────
  function pageTitle() {
    if (occasionFilter) {
      const occ = OCCASIONS.find((o) => o.value === occasionFilter);
      return occ ? `${occ.label} Products` : "Products";
    }
    if (selectedCategory !== "All") return selectedCategory;
    if (searchParams.has("minPrice")) {
      const hi = parseFloat(searchParams.get("maxPrice") || "999999");
      return hi >= 9999 ? "Products $100+" : `Products $${searchParams.get("minPrice")} – $${searchParams.get("maxPrice")}`;
    }
    return "All Products";
  }

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen page-enter" style={{ backgroundColor: "#FFFBF8" }}>

      {/* ── Page header ──────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-gray-400 mb-3">
            <Link to="/" className="flex items-center gap-1 hover:text-rose-500 transition-colors">
              <HomeOutlinedIcon sx={{ fontSize: 14 }} /> Home
            </Link>
            <span>/</span>
            <span className="text-gray-700 font-medium">{pageTitle()}</span>
          </nav>

          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
            <div>
              <h1 className="font-display text-3xl sm:text-4xl font-bold text-gray-900">
                {pageTitle()}
              </h1>
              <p className="text-gray-500 text-sm mt-1">
                {loading ? "Loading products…" : `${filtered.length} product${filtered.length !== 1 ? "s" : ""} found`}
              </p>
            </div>

            {/* Sort + Filter toggles */}
            <div className="flex items-center gap-2">
              {/* Sort dropdown */}
              <div className="relative">
                <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-700 cursor-pointer hover:border-rose-300 transition-colors">
                  <SortOutlinedIcon sx={{ fontSize: 16 }} />
                  <select
                    value={sortBy}
                    onChange={(e) => setParam("sort", e.target.value)}
                    className="bg-transparent text-sm text-gray-700 font-medium cursor-pointer focus:outline-none pr-1"
                  >
                    {SORT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Filter toggle (mobile) */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold border transition-all
                  ${showFilters
                    ? "bg-rose-500 text-white border-rose-500"
                    : "bg-white text-gray-700 border-gray-200 hover:border-rose-300"
                  }`}
              >
                <TuneIcon sx={{ fontSize: 16 }} />
                Filters
                {hasFilters && <span className="w-1.5 h-1.5 bg-white rounded-full ml-0.5" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* ── Filter panel ──────────────────────────────────────────── */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden mb-8"
            >
              <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-card">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">

                  {/* Category */}
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3 flex items-center gap-1.5">
                      <FilterListIcon sx={{ fontSize: 14 }} /> Category
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {CATEGORIES.map(({ label, Icon }) => (
                        <button
                          key={label}
                          onClick={() => setParam("category", label)}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all
                            ${selectedCategory === label
                              ? "bg-rose-500 text-white border-rose-500"
                              : "bg-white text-gray-600 border-gray-200 hover:border-rose-300 hover:text-rose-600"
                            }`}
                        >
                          <Icon sx={{ fontSize: 13 }} /> {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Occasion */}
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3 flex items-center gap-1.5">
                      <CelebrationOutlinedIcon sx={{ fontSize: 14 }} /> Occasion
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {OCCASIONS.map(({ label, value, Icon }) => (
                        <button
                          key={value}
                          onClick={() => setParam("occasion", occasionFilter === value ? "" : value)}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all
                            ${occasionFilter === value
                              ? "bg-rose-500 text-white border-rose-500"
                              : "bg-white text-gray-600 border-gray-200 hover:border-rose-300 hover:text-rose-600"
                            }`}
                        >
                          <Icon sx={{ fontSize: 13 }} /> {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Price Range */}
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3 flex items-center gap-1.5">
                      <AttachMoneyIcon sx={{ fontSize: 14 }} /> Price Range
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {PRICE_RANGES.map(({ label, min, max }) => {
                        const active = minPrice === min && maxPrice === max;
                        return (
                          <button
                            key={label}
                            onClick={() => setPriceRange(min, max)}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all
                              ${active
                                ? "bg-rose-500 text-white border-rose-500"
                                : "bg-white text-gray-600 border-gray-200 hover:border-rose-300 hover:text-rose-600"
                              }`}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Clear filters */}
                {hasFilters && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <button
                      onClick={clearAllFilters}
                      className="inline-flex items-center gap-1.5 text-xs text-rose-600 font-semibold hover:text-rose-700 transition-colors"
                    >
                      <CloseIcon sx={{ fontSize: 14 }} /> Clear all filters
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Active filter pills ────────────────────────────────────── */}
        {hasFilters && (
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <span className="text-xs text-gray-400 font-medium">Active filters:</span>
            {selectedCategory !== "All" && (
              <button
                onClick={() => setParam("category", "All")}
                className="inline-flex items-center gap-1 bg-rose-50 text-rose-700 text-xs font-semibold px-3 py-1 rounded-full border border-rose-100 hover:bg-rose-100 transition-colors"
              >
                {selectedCategory} <CloseIcon sx={{ fontSize: 12 }} />
              </button>
            )}
            {occasionFilter && (
              <button
                onClick={() => setParam("occasion", "")}
                className="inline-flex items-center gap-1 bg-purple-50 text-purple-700 text-xs font-semibold px-3 py-1 rounded-full border border-purple-100 hover:bg-purple-100 transition-colors"
              >
                {OCCASIONS.find((o) => o.value === occasionFilter)?.label} <CloseIcon sx={{ fontSize: 12 }} />
              </button>
            )}
            {searchParams.has("minPrice") && (
              <button
                onClick={() => setPriceRange(0, 999999)}
                className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 text-xs font-semibold px-3 py-1 rounded-full border border-amber-100 hover:bg-amber-100 transition-colors"
              >
                ${searchParams.get("minPrice")} – ${parseFloat(searchParams.get("maxPrice")) >= 9999 ? "100+" : searchParams.get("maxPrice")}
                <CloseIcon sx={{ fontSize: 12 }} />
              </button>
            )}
          </div>
        )}

        {/* ── Product grid ──────────────────────────────────────────── */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => <ProductSkeleton key={i} />)}
          </div>
        ) : error ? (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-2xl p-10 text-center">
            <p className="font-semibold text-lg mb-1">Something went wrong</p>
            <p className="text-sm">{error}</p>
          </div>
        ) : filtered.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-24">
            <SearchOffIcon sx={{ fontSize: 56, color: "#d1d5db", marginBottom: 2 }} />
            <p className="font-display text-2xl font-bold text-gray-900 mb-2">No products found</p>
            <p className="text-gray-500 mb-6">Try changing your filters or browse all categories</p>
            <button
              onClick={clearAllFilters}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-rose-500 to-pink-600 text-white font-semibold px-6 py-3 rounded-2xl hover:opacity-90 transition shadow-gift-sm"
            >
              Browse All Products <ArrowForwardIcon sx={{ fontSize: 16 }} />
            </button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
